/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Upload, FileCheck2, FileText, Trash2 } from 'lucide-react';
import { uploadIseeAttestazione, deleteIseeAttestazione, fileToBase64 } from '@/lib/pricing';

/**
 * IseeAttestazioneUpload — Sprint 9F: upload/replace/remove del PDF
 * dell'attestazione ISEE INPS per la verifica.
 * Extracted from IseeBundlePanel.jsx (Code Quality refactor).
 *
 * @param {object}   iseeState  status.isee (attestazione, verification_status, verification_note)
 * @param {function} onChanged  callback per ricaricare lo stato dopo upload/rimozione
 */
export const IseeAttestazioneUpload = ({ iseeState, onChanged }) => {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const attestUploaded = !!iseeState?.attestazione?.filename;
  const vStatus = iseeState?.verification_status;

  const handleFilePick = () => fileInputRef.current?.click();

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      return toast.error('Solo file PDF accettati');
    }
    if (file.size > 4 * 1024 * 1024) {
      return toast.error('Il file supera i 4 MB');
    }
    setUploading(true);
    try {
      const dataUri = await fileToBase64(file);
      await uploadIseeAttestazione(dataUri, file.name, file.type || 'application/pdf');
      toast.success('Attestazione caricata — verifica in corso');
      await onChanged?.();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Errore caricamento'); }
    finally { setUploading(false); }
  };

  const handleRemove = async () => {
    try { await deleteIseeAttestazione(); toast.success('Attestazione rimossa'); await onChanged?.(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Errore'); }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, height: 0 }}
        animate={{ opacity: 1, height: 'auto' }}
        className="mt-3 rounded-2xl border border-primary/25 bg-primary/5 p-3"
        data-testid="attestazione-upload-block"
      >
        <div className="flex items-start gap-3 flex-wrap">
          <div className="h-9 w-9 rounded-xl bg-primary/20 text-primary flex items-center justify-center flex-shrink-0">
            <FileText className="h-4 w-4" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-sm">Attestazione ISEE (opzionale)</div>
            <div className="text-xs text-muted-foreground leading-snug mt-0.5">
              Carica il PDF dell'attestazione ISEE INPS per la verifica. Ti sarà dato un <b>badge verificato</b> visibile ai medici.
            </div>
            {attestUploaded && (
              <div className="mt-2 flex items-center gap-2 text-xs" data-testid="attestazione-current">
                <FileCheck2 className="h-3.5 w-3.5 text-emerald-600" />
                <span className="truncate">{iseeState.attestazione.filename}</span>
                <span className="text-muted-foreground">• {new Date(iseeState.attestazione.uploaded_at).toLocaleDateString('it')}</span>
              </div>
            )}
            {vStatus === 'rejected' && iseeState.verification_note && (
              <div className="mt-2 text-xs text-red-600" data-testid="verification-note-rejected">
                <b>Motivo rigetto:</b> {iseeState.verification_note}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf,.pdf"
              className="hidden"
              onChange={handleFileChange}
              data-testid="attestazione-file-input"
            />
            <Button size="sm" variant="outline" onClick={handleFilePick} disabled={uploading}
              data-testid="attestazione-upload-btn">
              <Upload className="h-3.5 w-3.5" />
              {uploading ? 'Caricamento…' : attestUploaded ? 'Sostituisci' : 'Carica PDF'}
            </Button>
            {attestUploaded && (
              <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive"
                onClick={handleRemove} data-testid="attestazione-remove-btn">
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};
